import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  loadChannels,
  loadChannelsFailure,
  loadChannelsSuccess,
  markAsVisitedFailure,
  markAsVisitedSuccess,
  subscribeChannelFailure,
  subscribeChannelSuccess,
  unsubscribeChannelFailure,
  unsubscribeChannelSuccess,
} from './channels.actions';
import { Channel } from './channels.models';

export const NEWSFEED_CHANNELS_FEATURE_KEY = 'newsfeedChannels';

export interface NewsfeedChannelsState extends EntityState<Channel> {
  loaded: boolean;
  error?: string | null;
}

export interface NewsfeedChannelsPartialState {
  readonly [NEWSFEED_CHANNELS_FEATURE_KEY]: NewsfeedChannelsState;
}

export function selectChannelId(channel: Channel) {
  return channel.id;
}

export function selectSponsoredChannel(channel: Channel) {
  return channel.state.includes('SPONSORED');
}

export function selectSubscribedChannel(channel: Channel) {
  return !selectSponsoredChannel(channel);
}

export function selectValidChannel(channel: Channel) {
  return !channel.state.includes('NOT_FOUND');
}

export const newsfeedChannelsAdapter: EntityAdapter<Channel> = createEntityAdapter<Channel>({
  selectId: selectChannelId,
});

const { selectAll } = newsfeedChannelsAdapter.getSelectors();

export const initialNewsfeedChannelsState: NewsfeedChannelsState = newsfeedChannelsAdapter.getInitialState({
  loaded: false,
});

const reducer = createReducer(
  initialNewsfeedChannelsState,
  on(loadChannels, (state) => ({ ...state, loaded: false, error: null })),
  on(loadChannelsSuccess, (state, { channels }) =>
    newsfeedChannelsAdapter.setAll(channels, { ...state, loaded: true })
  ),
  on(subscribeChannelSuccess, (state, { channel }) =>
    newsfeedChannelsAdapter.setAll([channel, ...selectAll(state)], state)
  ),
  on(markAsVisitedSuccess, (state, { channel }) => newsfeedChannelsAdapter.setOne(channel, state)),
  on(unsubscribeChannelSuccess, (state, { channel }) =>
    newsfeedChannelsAdapter.removeOne(selectChannelId(channel), state)
  ),
  on(
    loadChannelsFailure,
    subscribeChannelFailure,
    unsubscribeChannelFailure,
    markAsVisitedFailure,
    (state, { error }) => ({
      ...state,
      error,
    })
  )
);

export function newsfeedChannelsReducer(state: NewsfeedChannelsState | undefined, action: Action) {
  return reducer(state, action);
}
